import type { LocationQueryRaw, NavigationGuard } from 'vue-router';

import { captureUTMParams } from '@/services/utm/utmTrackingService';

const getQueryParamsFromUrl = (): LocationQueryRaw => {
  if (!window.location.search) return {};

  const queryParams: LocationQueryRaw = {};
  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    queryParams[key] = value;
  });

  return queryParams;
};

/**
 * In hash-mode routing, window.location.search (everything before '#') is only
 * ever touched by a full page load — router navigation only rewrites the hash.
 * Left alone, campaign params would stay pinned to the address bar for the rest
 * of the session regardless of where the user navigates. Strip them immediately
 * after capture, using replaceState so no extra history entry is created and the
 * hash (current route) is left untouched.
 */
const stripUTMParamsFromUrl = (): void => {
  if (!window.location.search) return;

  const url = new URL(window.location.href);
  // Snapshot keys first — mutating searchParams while iterating it is unsafe.
  const utmKeys = Array.from(url.searchParams.keys()).filter((key) =>
    key.startsWith('utm_')
  );

  if (!utmKeys.length) return;

  utmKeys.forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(history.state, '', url.toString());
};

/**
 * Captures email campaign UTM parameters on the first navigation.
 *
 * Must run before auth redirects so campaign params are preserved.
 */
const utmCaptureGuard: NavigationGuard = (to, from) => {
  // Only capture on initial navigation to avoid overwriting attribution during in-app routing.
  if (from.matched.length !== 0) return;

  // Prefer parsed router query, but fall back to raw URL parsing in hash-mode edge cases.
  const queryParams =
    Object.keys(to.query || {}).length > 0 ? to.query : getQueryParamsFromUrl();

  captureUTMParams(queryParams);
  stripUTMParamsFromUrl();
};

export default utmCaptureGuard;
